import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HelpCircle, Paperclip, Download, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getCteDetalhe,
  formatDateTime,
  formatDate,
  uploadComprovantesCte,
  deleteComprovante,
  comprovanteDownloadUrl,
  comprovantesZipUrl,
} from "@/lib/ocorenApi";
import { getCategoria } from "@/lib/categorias";
import { ComprovantesDialog } from "@/components/ComprovantesDialog";

/**
 * Lista as NFs vinculadas a um CT-e (consulta em tempo real ao MySQL externo
 * + status de cada NF no nosso próprio banco). Usado como conteúdo da linha
 * expansível na tabela de CT-es.
 */
export const CteNfsList = ({ numeroCte, cnpjTransportadora, onShowTimeline }) => {
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const navigate = useNavigate();
  const [compNota, setCompNota] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      await uploadComprovantesCte(numeroCte, [], files);
      toast.success(`${files.length} arquivo(s) anexado(s) ao CT-e`);
      const res = await getCteDetalhe(numeroCte, cnpjTransportadora);
      setDetalhe(res);
    } catch {
      toast.error("Falha ao anexar arquivos");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente remover este comprovante?")) return;
    try {
      await deleteComprovante(id);
      toast.success("Comprovante removido do CT-e");
      const res = await getCteDetalhe(numeroCte, cnpjTransportadora);
      setDetalhe(res);
    } catch {
      toast.error("Falha ao remover comprovante");
    }
  };

  useEffect(() => {
    if (!numeroCte) return;
    setLoading(true);
    setErro(false);
    getCteDetalhe(numeroCte, cnpjTransportadora)
      .then((res) => setDetalhe(res))
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, [numeroCte, cnpjTransportadora]);

  const handleTimelineClick = (nf) => {
    if (onShowTimeline) {
      const mov = {
        ...nf,
        numero_nota: nf.nfe_numero,
      };
      onShowTimeline(mov);
    }
  };

  if (loading) {
    return (
      <div className="text-xs text-[#52525B] py-4 text-center">
        Carregando NFs do CT-e…
      </div>
    );
  }

  if (erro || !detalhe) {
    return (
      <div className="text-xs text-[#52525B] py-4 text-center">
        Não foi possível carregar o detalhe deste CT-e no banco de vínculos.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Tabela de NFs (2/3 de largura) */}
      <div className="lg:col-span-2 space-y-2">
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold mb-2">
          NFs vinculadas ao CT-e {detalhe.cte_numero} ({detalhe.nfs.length})
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[#52525B]">
                <th className="pb-1.5 font-semibold">NF</th>
                <th className="pb-1.5 font-semibold">Status (nosso sistema)</th>
                <th className="pb-1.5 font-semibold">Última atualização</th>
                <th className="pb-1.5 font-semibold text-center">Eventos</th>
                <th className="pb-1.5 font-semibold">Previsão entrega (CT-e)</th>
                <th className="pb-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {detalhe.nfs.map((nf, i) => {
                const cat = nf.categoria ? getCategoria(nf.categoria) : null;
                const Icon = cat ? cat.icon : HelpCircle;
                return (
                  <tr key={i} className="border-t border-[#F4F4F5]">
                    <td className="py-1.5 font-mono-data font-semibold text-[#09090B]">
                      <span className="inline-flex items-center gap-1.5">
                        {nf.nfe_numero}
                        {nf.qtd_comprovantes > 0 && (
                          <button
                            data-testid={`cte-nf-comprovante-badge-${i}`}
                            title={`${nf.qtd_comprovantes} comprovante(s) — clique para ver/baixar`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCompNota({
                                numero_nota: nf.nfe_numero,
                                cnpj_cliente: nf.cnpj_cliente,
                              });
                            }}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer"
                          >
                            <Paperclip size={10} />
                            {nf.qtd_comprovantes}
                          </button>
                        )}
                      </span>
                    </td>
                    <td className="py-1.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${
                          cat ? `${cat.bg} ${cat.text} ${cat.border}` : "bg-zinc-100 text-zinc-500 border-zinc-200"
                        }`}
                      >
                        <Icon size={11} />
                        {cat ? cat.label : "Sem rastreio"}
                      </span>
                    </td>
                    <td className="py-1.5 font-mono-data text-[#52525B] whitespace-nowrap">
                      {formatDateTime(nf.ultima_data)}
                    </td>
                    <td className="py-1.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 text-[11px] font-bold font-mono-data bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm">
                        {nf.total_eventos}
                      </span>
                    </td>
                    <td className="py-1.5 font-mono-data text-[#52525B] whitespace-nowrap">
                      {nf.dprev_entrega ? formatDate(nf.dprev_entrega) : "—"}
                    </td>
                    <td className="py-1.5 text-right">
                      {nf.total_eventos > 0 ? (
                        <button
                          data-testid={`cte-nf-ver-timeline-${i}`}
                          onClick={() => handleTimelineClick(nf)}
                          className="text-[11px] font-semibold text-emerald-600 hover:text-emerald-700 underline hover:no-underline whitespace-nowrap"
                        >
                          Ver Histórico
                        </button>
                      ) : (
                        <span className="text-[11px] text-[#A1A1AA] whitespace-nowrap">
                          Sem histórico
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seção Lateral: Arquivos do CT-e (1/3 de largura) */}
      <div className="space-y-4 bg-white p-4 border border-[#E4E4E7] rounded-md h-fit">
        <div className="flex items-center justify-between border-b border-[#F4F4F5] pb-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-[#52525B] uppercase tracking-[0.1em]">
            <Paperclip size={14} />
            Arquivos do CT-e
          </div>
          {detalhe.comprovantes && detalhe.comprovantes.length > 0 && (
            <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
              {detalhe.comprovantes.length}
            </span>
          )}
        </div>

        {/* Input de Upload direto */}
        <div>
          <label className="block text-[9px] uppercase tracking-[0.1em] text-[#52525B] font-bold mb-1">
            Anexar Arquivo ao CT-e
          </label>
          <input
            type="file"
            multiple
            disabled={uploading}
            onChange={handleUpload}
            className="w-full text-xs file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border file:border-[#E4E4E7] file:text-[11px] file:font-semibold file:bg-[#FAFAFA] file:text-[#09090B] hover:file:bg-[#F4F4F5] file:cursor-pointer cursor-pointer"
          />
          {uploading && <div className="text-[10px] text-zinc-500 mt-1">Enviando arquivos...</div>}
        </div>

        {/* Lista de comprovantes diretos */}
        <div className="space-y-2 max-h-48 overflow-y-auto pt-1">
          {!detalhe.comprovantes || detalhe.comprovantes.length === 0 ? (
            <div className="text-xs text-[#A1A1AA] text-center py-4">
              Nenhum comprovante anexado diretamente a este CT-e.
            </div>
          ) : (
            detalhe.comprovantes.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between p-2 rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] text-xs"
              >
                <div className="min-w-0 flex-1 pr-2">
                  <div
                    title={c.original_filename}
                    className="font-medium text-[#09090B] truncate"
                  >
                    {c.original_filename}
                  </div>
                  <div className="text-[9px] text-[#A1A1AA] mt-0.5">
                    {formatDateTime(c.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a
                    href={comprovanteDownloadUrl(c.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1 rounded-sm text-zinc-500 hover:text-[#09090B] hover:bg-zinc-100 transition-colors"
                    title="Baixar arquivo"
                  >
                    <Download size={13} />
                  </a>
                  <button
                    onClick={() => handleDelete(c.id)}
                    className="p-1 rounded-sm text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition-colors"
                    title="Excluir"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ComprovantesDialog
        open={!!compNota}
        onOpenChange={(open) => !open && setCompNota(null)}
        nota={compNota}
        onChanged={() => {
          getCteDetalhe(numeroCte, cnpjTransportadora).then((res) => setDetalhe(res));
        }}
      />
    </div>
  );
};

export default CteNfsList;
